import { useState } from 'react';
import EverTrue from 'app';
import useCadenceMutations from './use-cadence-mutations';
import { isBulkMessageType } from '../utils/cadence-enums';

/**
 * Shared action handlers for cadence detail pages.
 * Consolidates rename, duplicate, save-as-shell, and add-people logic
 * that is duplicated across CadenceDetailPage, BulkMessageDetailPage,
 * and TemplateDetailPage.
 *
 * @param {Object} params
 * @param {string|number} params.entityId - The cadence/message/template ID
 * @param {Object} params.entity - The mapped entity object (needs `.name`)
 * @param {Object} params.rawEntity - The raw API entity (needs `.version`)
 * @param {Function} params.refetch - Callback to reload entity data after mutations
 */
const useDetailPageActions = ({ entityId, entity, rawEntity, refetch, entityType }) => {
  const { updateCadence, duplicateCadence, enrollMember, saveCadenceAsTemplate } = useCadenceMutations();

  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showBlueprintModal, setShowBlueprintModal] = useState(false);

  const handleStartRename = () => {
    setEditName(entity?.name || '');
    setIsEditingName(true);
  };

  const handleCancelRename = () => {
    setIsEditingName(false);
  };

  const handleSubmitRename = async () => {
    const newName = editName.trim();
    setIsEditingName(false);
    if (!newName || newName === entity?.name) return;
    try {
      await updateCadence(entityId, rawEntity?.version, { name: newName });
      EverTrue.Alert.success('Renamed successfully');
      refetch();
    } catch (err) {
      EverTrue.Alert.error(`Rename failed: ${err.message}`);
    }
  };

  const handleDuplicate = () => {
    setShowDuplicateModal(true);
  };

  const handleDuplicateConfirm = async (newName, options) => {
    try {
      const newCadence = await duplicateCadence(entityId, newName, options);
      EverTrue.Alert.success('Duplicated successfully');
      setShowDuplicateModal(false);
      if (newCadence?.id) {
        const type = entityType || rawEntity?.cadenceType;
        if (isBulkMessageType(type)) {
          EverTrue.Navigator(`/cadences-v2/bulk-message/${newCadence.id}`, { trigger: true });
        } else if (rawEntity?.isTemplate) {
          EverTrue.Navigator(`/cadences-v2/template/${newCadence.id}`, { trigger: true });
        } else {
          EverTrue.Navigator(`/cadences-v2/${newCadence.id}`, { trigger: true });
        }
      }
    } catch (err) {
      if (err.blueprintFailed && err.duplicatedCadence?.id) {
        // Partial success: cadence was duplicated but saving as blueprint failed.
        EverTrue.Alert.warn(err.message);
        setShowDuplicateModal(false);
        EverTrue.Navigator(`/cadences-v2/${err.duplicatedCadence.id}`, { trigger: true });
      } else {
        EverTrue.Alert.error(`Duplicate failed: ${err.message}`);
        throw err;
      }
    }
  };

  const handleShowShellInput = () => {
    setShowBlueprintModal(true);
  };

  const handleSaveAsBlueprintConfirm = async (blueprintName, _options) => {
    try {
      await saveCadenceAsTemplate(entityId, { name: blueprintName });
      EverTrue.Alert.success(`Saved as cadence blueprint "${blueprintName}"`);
      setShowBlueprintModal(false);
    } catch (err) {
      EverTrue.Alert.error(`Save as blueprint failed: ${err.message}`);
      throw err;
    }
  };

  const handleAddPeople = async (contacts) => {
    let enrolled = 0;
    const errors = [];
    for (const contact of contacts) {
      try {
        await enrollMember(entityId, contact.id);
        enrolled++;
      } catch (err) {
        errors.push(err.message || 'Failed to enroll contact');
      }
    }
    if (enrolled > 0) {
      EverTrue.Alert.success(`Enrolled ${enrolled} contact(s)`);
      refetch();
    }
    if (errors.length > 0) {
      // Show first unique error (de-dup identical messages from multi-select)
      const uniqueErrors = [...new Set(errors)];
      uniqueErrors.forEach((msg) => EverTrue.Alert.error(msg));
    }
    setShowContactPicker(false);
  };

  return {
    isEditingName,
    editName,
    setEditName,
    handleStartRename,
    handleCancelRename,
    handleSubmitRename,
    handleDuplicate,
    handleDuplicateConfirm,
    showDuplicateModal,
    setShowDuplicateModal,
    showBlueprintModal,
    setShowBlueprintModal,
    handleShowShellInput,
    handleSaveAsBlueprintConfirm,
    showContactPicker,
    setShowContactPicker,
    handleAddPeople,
  };
};

export default useDetailPageActions;
